import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MapPin } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip } from 'recharts';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import type { Device, Position, Geofence } from '@/lib/traccar-api';
import { isPointInGeofence } from '@/lib/geofence-utils';

interface DevicesInGeofencesCardProps {
  devices: Device[];
  positions: Position[];
  geofences: Geofence[];
}

const INSIDE_COLOR = 'hsl(142,76%,36%)';
const OUTSIDE_COLOR = 'hsl(220,13%,50%)';

export const DevicesInGeofencesCard = ({ devices, positions, geofences }: DevicesInGeofencesCardProps) => {
  const { t } = useTranslation();

  const insideCount = useMemo(() => {
    if (geofences.length === 0) return 0;
    const deviceIds = new Set(devices.map(d => d.id));
    let count = 0;
    for (const position of positions) {
      if (!deviceIds.has(position.deviceId)) continue;
      if (geofences.some(g => isPointInGeofence(position.latitude, position.longitude, g.area))) {
        count++;
      }
    }
    return count;
  }, [devices, positions, geofences]);

  const totalDevices = devices.length;
  const outsideCount = Math.max(totalDevices - insideCount, 0);

  const pieData = [
    { name: t('dashboard.inside'), value: insideCount, fillColor: INSIDE_COLOR },
    { name: t('dashboard.outside'), value: outsideCount, fillColor: OUTSIDE_COLOR },
  ].filter(d => d.value > 0);

  return (
    <Card className="h-full flex flex-col overflow-hidden">
      <CardHeader className="py-2 pl-4 pr-14 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${insideCount > 0 ? 'bg-primary/10' : 'bg-muted'}`}>
            <MapPin className={`h-3.5 w-3.5 ${insideCount > 0 ? 'text-primary' : 'text-muted-foreground'}`} />
          </div>
          <div>
            <CardTitle className="text-sm">{t('dashboard.devicesInGeofences')}</CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent className="py-2 px-4 flex-1 min-h-0">
        {pieData.length === 0 ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-xs">
            {t('dashboard.noDevicesInGeofences')}
          </div>
        ) : (
          <div className="flex items-center gap-3 h-full">
            <div className="relative flex-shrink-0">
              <PieChart width={100} height={100}>
                <Pie
                  data={pieData}
                  cx={45}
                  cy={45}
                  innerRadius={30}
                  outerRadius={45}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fillColor} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
              </PieChart>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <span className="text-lg font-bold">{insideCount}</span>
                  <span className="text-[10px] text-muted-foreground block">
                    {t('dashboard.inside').toLowerCase()}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex-1 space-y-1 overflow-hidden">
              {[
                { label: t('dashboard.inside'), count: insideCount, color: 'bg-[hsl(142,76%,36%)]' },
                { label: t('dashboard.outside'), count: outsideCount, color: 'bg-[hsl(220,13%,50%)]' },
              ].map((row) => {
                const percentage = totalDevices > 0 ? Math.round((row.count / totalDevices) * 100) : 0;
                return (
                  <div key={row.label} className="w-full flex items-center gap-2 text-xs py-0.5 px-1">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${row.color}`} />
                    <span className="text-muted-foreground truncate flex-1 text-left">{row.label}</span>
                    <span className="font-medium">{row.count} ({percentage}%)</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
